import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { IBAN_COMPONENT_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds.ts';
import { Button, Flex, Loader, Paper, Text, useMantineTheme } from '@mantine/core';
import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TextInput } from 'react-hook-form-mantine';
import { SummaryCard } from '@dealstack/frontend/shared/components/SummaryCard/SummaryCard.tsx';
import { SummaryCardRow } from '@dealstack/frontend/shared/components/SummaryCard/SummaryCard.types.ts';
import { useBankAccountFormContext } from '../../BankAccountFormContext.tsx';
import { useBankAccountVerificationContext } from '../../BankAccountVerificationContext.tsx';
import { BANK_ACCOUNT_FORM_FIELDS, BankAccountFormValues } from '../../types.ts';

export const IbanFieldset = () => {
	const tCapManage = useTranslationCapManage();
	const theme = useMantineTheme();
	const { control } = useFormContext<BankAccountFormValues>();
	const {
		hasEditPermission,
		fieldsDisabled,
		bankAccount,
		storedSummaryValidated,
		onManualEntrySwitch,
	} = useBankAccountFormContext();
	const verification = useBankAccountVerificationContext();
	const { status, result, ibanMatchesStored } = verification;

	const [manualEntryRequested, setManualEntryRequested] = useState(false);
	const accountNumber = useWatch({ control, name: BANK_ACCOUNT_FORM_FIELDS.account_number });
	const bic = useWatch({ control, name: BANK_ACCOUNT_FORM_FIELDS.bic });

	// While the saved account's IBAN is untouched, its stored details stand in for a
	// lookup summary; editing the IBAN drops back into the check/lookup flow.
	const storedDetails =
		ibanMatchesStored && bankAccount
			? {
					bankName: bankAccount.bankName,
					accountNumber: bankAccount.accountNumber,
					bic: bankAccount.swiftCode,
				}
			: null;
	const hasStoredSummary = !!(
		storedDetails &&
		(storedDetails.bankName || storedDetails.accountNumber || storedDetails.bic)
	);
	const storedSummarySource = status === 'idle' && hasStoredSummary ? storedDetails : null;
	const summarySource = status === 'success' ? result : storedSummarySource;
	// the badge only vouches for a lookup that ran in this session
	const isVerifiedSummary = status === 'success';

	// With a manual-entry format tab available, the fallback switches tabs and the
	// inline fields never render; without one they remain the only manual path.
	const manualEntryOpen =
		!onManualEntrySwitch &&
		(manualEntryRequested ||
			(status !== 'success' && !hasStoredSummary && !!(accountNumber || bic)));

	const summaryRows = summarySource
		? [
				{
					label: tCapManage('bank_account.drawer.bank_name.label'),
					value: summarySource.bankName,
				},
				{
					label: tCapManage('bank_account.drawer.account_number.label'),
					value: summarySource.accountNumber,
				},
				{
					label: tCapManage('bank_account.drawer.bic.label'),
					value: summarySource.bic,
				},
			].filter((row): row is SummaryCardRow => !!row.value)
		: [];

	return (
		<>
			<TextInput
				control={control}
				name={BANK_ACCOUNT_FORM_FIELDS.iban}
				label={tCapManage('bank_account.drawer.iban.label')}
				placeholder={tCapManage('bank_account.drawer.iban.placeholder')}
				size="md"
				withAsterisk
				disabled={!hasEditPermission}
				data-testid={IBAN_COMPONENT_ELEMENT_ID.IBAN_INPUT}
			/>

			{status === 'idle' && !hasStoredSummary && hasEditPermission && (
				<Button
					variant="transparent"
					w="fit-content"
					onClick={verification.onVerify}
					disabled={verification.verifyDisabled}
					data-testid={IBAN_COMPONENT_ELEMENT_ID.LOOKUP_BUTTON}
				>
					{tCapManage('bank_account.drawer.check_account_button')}
				</Button>
			)}

			{status === 'loading' && (
				<Flex
					align="center"
					gap="0.5rem"
					data-testid={IBAN_COMPONENT_ELEMENT_ID.IBAN_LOOKUP_LOADING}
				>
					<Loader variant="oval" size="xs" />
					<Text size="sm" c="dimmed">
						{tCapManage('bank_account.drawer.iban_lookup_loading')}
					</Text>
				</Flex>
			)}

			{summarySource && (
				<SummaryCard
					title={tCapManage('bank_account.drawer.iban_lookup_success.title')}
					badgeLabel={
						isVerifiedSummary
							? tCapManage('bank_account.drawer.iban_lookup_success.badge')
							: undefined
					}
					rows={summaryRows}
					emptyText={tCapManage('bank_account.drawer.verification_success.no_details', {
						defaultValue:
							'Verified with the bank directory. It returned no further details.',
					})}
					// Green only for a check that still applies: one that ran in this
					// session, or stored details under their own country and format.
					highlighted={isVerifiedSummary || storedSummaryValidated}
					dataTestId={IBAN_COMPONENT_ELEMENT_ID.IBAN_LOOKUP_SUMMARY}
				/>
			)}

			{status === 'error' && (
				<>
					<Paper
						withBorder
						p="0.5rem"
						data-testid={IBAN_COMPONENT_ELEMENT_ID.IBAN_LOOKUP_ERROR}
						style={{ borderColor: theme.colors.orange[2] }}
					>
						<Flex gap="1rem">
							<Icon
								variant="warning"
								size="xl"
								style={{ color: theme.colors.orange[6] }}
							/>
							<Flex direction="column" gap="0.25rem">
								<Text fw={600}>
									{tCapManage('bank_account.drawer.iban_lookup_failed.title')}
								</Text>
								<Text fw={400}>
									{tCapManage('bank_account.drawer.iban_lookup_failed.text')}
								</Text>
							</Flex>
						</Flex>
					</Paper>

					{!manualEntryOpen && hasEditPermission && (
						<Flex direction="column">
							<Text size="sm">
								{tCapManage('bank_account.drawer.manual_entry.prompt')}
							</Text>
							<Button
								variant="transparent"
								px={0}
								w="fit-content"
								onClick={() =>
									onManualEntrySwitch
										? onManualEntrySwitch()
										: setManualEntryRequested(true)
								}
								data-testid={IBAN_COMPONENT_ELEMENT_ID.IBAN_MANUAL_ENTRY_BUTTON}
							>
								{tCapManage('bank_account.drawer.manual_entry.button')}
							</Button>
						</Flex>
					)}
				</>
			)}

			{manualEntryOpen && (
				<>
					<TextInput
						control={control}
						name={BANK_ACCOUNT_FORM_FIELDS.account_number}
						label={tCapManage('bank_account.drawer.account_number.label')}
						placeholder={tCapManage('bank_account.drawer.account_number.placeholder')}
						size="md"
						disabled={fieldsDisabled}
						data-testid={IBAN_COMPONENT_ELEMENT_ID.ACCOUNT_NUMBER_INPUT}
					/>

					<TextInput
						control={control}
						name={BANK_ACCOUNT_FORM_FIELDS.bic}
						label={tCapManage('bank_account.drawer.bic.label')}
						placeholder={tCapManage('bank_account.drawer.bic.placeholder')}
						size="md"
						disabled={fieldsDisabled}
						data-testid={IBAN_COMPONENT_ELEMENT_ID.BIC_INPUT}
					/>
				</>
			)}
		</>
	);
};
